import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { CTASection } from '../components/sections/CTASection';
import { Award, Users, Globe, Target, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const milestones = [
  { year: '2009', title: 'Journey Begins', description: 'Started our journey in the exhibition and events industry with a passion for creating exceptional experiences.' },
  { year: '2012', title: 'First Major Project', description: 'Successfully executed our first large-scale trade fair booth for a Fortune 500 company.' },
  { year: '2015', title: 'Expanded Services', description: 'Added corporate events and brand activation to our service portfolio.' },
  { year: '2018', title: 'Pan-India Presence', description: 'Extended operations to 10+ cities across India with a growing team of experts.' },
  { year: '2020', title: 'DFAIRS Incorporated', description: 'Officially registered as DFAIRS Communications (OPC) Private Limited in Telangana.' },
  { year: '2023', title: 'Corporate Partnerships', description: 'Partnered with leading brands including Darwinbox, Nexus, and Bajaj Electronics.' },
  { year: '2024', title: 'Flagship Events Launch', description: 'Launched India Retail Investors Expo and The FinRun marathon events.' },
  { year: '2025', title: 'Global Vision', description: 'Setting sights on international markets with global standard services.' },
];

const values = [
  { 
    icon: Award, 
    title: 'Excellence', 
    description: 'We deliver nothing less than exceptional quality in every project we undertake.' 
  },
  { 
    icon: Users, 
    title: 'Collaboration', 
    description: 'We believe in building strong partnerships with our clients and stakeholders.' 
  },
  { 
    icon: Globe, 
    title: 'Global Standards', 
    description: 'We benchmark ourselves against the best in the world to deliver world-class experiences.' 
  },
  { 
    icon: Target, 
    title: 'Innovation', 
    description: 'We constantly push boundaries to create unique and memorable event experiences.' 
  },
];

export default function AboutPage() {
  return (
    <div className="page-transition" data-testid="about-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://images.unsplash.com/photo-1740727263760-d220d5a90d9f?auto=format&fit=crop&w=2000&q=80"
              alt="Background"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Our Story
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Simplifying Trade Fairs, <span className="text-gold">Creating Experiences</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                With over 15 years of industry expertise since 2009, DFAIRS Communications is a world-class Exhibition and Corporate Event Management company, dedicated to delivering exceptional experiences that elevate brands and create lasting impressions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?auto=format&fit=crop&w=800&q=80"
                  alt="DFAIRS Team"
                  className="w-full h-[500px] object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="mb-12">
                  <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">Our Mission</h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    To simplify trade fairs and event management by providing comprehensive, innovative solutions that help brands connect with their audiences effectively and create memorable experiences.
                  </p>
                </div>
                <div>
                  <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">Our Vision</h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    To become a global leader in exhibition and event management, recognized for our commitment to excellence, innovation, and value creation for all stakeholders.
                  </p>
                  <div className="space-y-3">
                    {['Global expansion by 2030', 'Industry-leading innovation', 'Sustainable event practices'].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-gold" />
                        <span className="text-navy">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 lg:py-32 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                What Drives Us
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy">
                Our Core Values
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 hover-lift"
                  data-testid={`value-${index}`}
                >
                  <div className="w-14 h-14 bg-navy/5 flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-gold" />
                  </div>
                  <h3 className="font-heading text-xl text-navy mb-3">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Our Journey
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy">
                Milestones & Achievements
              </h2>
            </motion.div>

            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gold/20 hidden lg:block" />

              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.year}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex flex-col lg:flex-row items-center gap-8 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="bg-muted p-8 inline-block">
                        <span className="font-heading text-4xl text-gold">{milestone.year}</span>
                        <h3 className="font-heading text-xl text-navy mt-2 mb-2">{milestone.title}</h3>
                        <p className="text-muted-foreground">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="w-6 h-6 bg-gold rounded-full border-4 border-white shadow-lg z-10 hidden lg:block" />
                    <div className="lg:w-1/2" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-white mb-6"
            >
              Join Our Journey to Excellence
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-lg mb-10 max-w-2xl mx-auto"
            >
              Whether you're looking for a partner for your next event or want to be part of our growing team, we'd love to hear from you.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Get In Touch
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/careers" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors">
                Join Our Team
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
